import React, { useEffect, useState } from "react";

// Routing Components
import { Link, useLocation } from "react-router-dom";
import { Routes } from "@util";

// UI Elements
import { Divide as Hamburger } from "hamburger-react";
import { useDarkMode } from "@hooks/dark-mode";
import { BsSun } from "react-icons/bs";

// Props
import { useUser } from "@hooks/user";
import LanguageSwitcher from "../languageSwitch/LanguageSwitcher";
import { useTranslation } from "react-i18next";

// Custom Icons
const MoonIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    aria-hidden="true"
    focusable="false"
    height={size}
    role="presentation"
    viewBox="0 0 24 24"
    width={size}
  >
    <path
      d="M21.53 15.93c-.16-.27-.61-.69-1.73-.49a8.46 8.46 0 01-1.88.13 8.409 8.409 0 01-5.91-2.82 8.068 8.068 0 01-1.44-8.66c.44-1.01.13-1.54-.09-1.76s-.77-.55-1.83-.11a10.318 10.318 0 00-6.32 10.21 10.475 10.475 0 007.04 8.99 10 10 0 002.89.55c.16.01.32.02.48.02a10.5 10.5 0 008.47-4.27c.67-.93.49-1.519.32-1.79z"
      fill="currentColor"
    />
  </svg>
);
// All Links For The Header View
export const links = ["aboutUs", "pricing", "contact"];

export default function Header(props: Drivn.HeaderProps) {
  const { t } = useTranslation();

  /**
   * Navigate To A Specified Link Depending On The Nav Item
   *
   * @param link - The String Element Of The Button
   * @returns The Navigation Link To Navigate To
   */
  const handleNavigate = (value: string): string => {
    const key = value.toLowerCase();
    switch (key) {
      case "aboutus":
        return Routes.AboutUs.path;
      case "pricing":
        return Routes.Pricing.path;
      case "contact":
        return Routes.Contact.path;
      default:
        return "/";
    }
  };

  // Is The App Using Dark Mode
  const dark = useDarkMode();

  // The Current Location
  const location = useLocation();

  // The User
  const user = useUser();

  // Should the navbar show its background?
  const [showNavBg, setShowNavBg] = useState(location.pathname !== "/");

  // Listen to scroll to update navbar background visibility
  useEffect(() => {
    const handleScroll = () => {
      // Update navbar background visibility (only on home page)
      if (location.pathname === "/") {
        const shouldShowBg = window.scrollY > 10;
        setShowNavBg(shouldShowBg);
      }
    };

    // Initial state on mount
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Cleanup function to remove event listener
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [location.pathname]);

  // Ensure navbar is visible on all non-home routes
  useEffect(() => {
    if (location.pathname !== "/") {
      setShowNavBg(true);
    }
  }, [location.pathname]);

  // Get translated menu items
  const translatedLinks = links.map(link => ({
    original: link,
    translated: t(`navigation.${link}`)
  }));

  // Determine spacer height based on current route to avoid undesired gap on home page
  const spacerHeightClass = location.pathname === "/" ? "h-0" : "h-20";

  return (
    <section className={`${props.open ? "overflow-hidden" : ""} m-0 p-0 ${spacerHeightClass}`}>
      <nav
        className={`${props.open ? "bg-main" : showNavBg ? "bg-bg bg-opacity-90" : "bg-transparent"}
          transition-all w-screen flex items-center h-20 px-4 fixed top-0 z-50 m-0 p-0`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          // Force hardware acceleration
          transform: 'translateZ(0)',
          // Prevent unnecessary hardware acceleration when not needed
          willChange: 'auto',
          // Prevent visual glitches
          backfaceVisibility: 'hidden',
          transition: 'background-color 0.3s ease',
        }}
      >
        <div className="flex w-full items-center">
          <Link to="/" className="mr-4 ml-4 md:ml-10">
            <img src={dark.getImage()} alt="icon" className="w-30 h-10" />
          </Link>

          <ul className="hidden lg:flex items-center list-none text-gray-100 font-semibold">
            {translatedLinks.map(link => (
              <li key={link.original}>
                <Link
                  to={handleNavigate(link.original)}
                  className="p-2 px-3 mx-1 text-black hover:text-main transition-colors"
                >
                  {link.translated}
                </Link>
              </li>
            ))}
          </ul>

          <div className="ml-auto flex items-center">
            <div className="mr-4 hidden lg:block">
              <button
                onClick={dark.toggle}
                className="text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-4 focus:ring-gray-200 dark:focus:ring-gray-700 rounded-lg text-sm p-2.5"
              >
                {dark.dark ? <MoonIcon size={20} /> : <BsSun size={20} />}
              </button>
            </div>

            <div className="mr-4 hidden lg:block">
              <LanguageSwitcher variant="compact" />
            </div>

            <a
              href="https://app.drivn.dk/login"
              className="hidden lg:block mr-4 p-2 px-3 rounded-3xl text-bg bg-main hover:bg-white hover:text-main active:text-main active:bg-secBg"
            >
              {t('navigation.login')}
            </a>
          </div>

          <div className="lg:hidden ml-auto">
            <Hamburger
              toggled={props.open}
              toggle={() => props.toggleOpen(!props.open)}
              color={props.open ? "#111111" : dark.dark ? "#80ABF9" : "#1673FF"}
            />
          </div>
        </div>
      </nav>

      <div
        className={`${props.open
          ? "opacity-100 h-screen items-center mt-20 flex flex-col fixed top-0 left-0 right-0 bottom-0 bg-main z-40"
          : "pointer-events-none opacity-0"
          }`}
      >
        {translatedLinks.map(link => (
          <Link
            className="text-white justify-center text-3xl my-4 hover:text-bg font-semibold"
            key={link.original}
            to={handleNavigate(link.original)}
            onClick={() => props.toggleOpen(false)}
          >
            {link.translated}
          </Link>
        ))}

        <Link
          className="text-main bg-white text-3xl my-4 hover:text-white hover:bg-transparent font-semibold mt-6 border-2 border-white py-2 px-6 rounded-3xl transition-all"
          to={
            user.signedIn === true ? Routes.Dashboard.path : Routes.Signin.path
          }
          onClick={() => props.toggleOpen(false)}
        >
          {user.signedIn === true ? t('navigation.dashboard') : t('navigation.login')}
        </Link>

        <div className="mt-6">
          <LanguageSwitcher />
        </div>

        <div className="mt-6">
          <button
            onClick={dark.toggle}
            className="text-white text-3xl font-semibold p-4 rounded-lg hover:bg-white hover:text-main transition-all flex items-center justify-center"
          >
            {dark.dark ? <BsSun size={24} /> : <MoonIcon size={24} />}
          </button>
        </div>
      </div>
    </section>
  );
}
